import {
  Button,
  Header,
  Link,
  Modal,
  Popover,
  SpaceBetween,
  StatusIndicator,
  StatusIndicatorProps,
} from "@amzn/awsui-components-react";
import {
  formatDistanceToNow,
  formatDuration,
  intervalToDuration,
} from "date-fns";

import React, { useContext } from "react";
import { parseErrorDetails } from "../../helpers/errorUtils";
import { CanonicalBusinessEvent, ErrorDetails } from "../../types";
import { PublishStatus } from "../../types/cbes";
import { usePublishStatus } from "../../hooks/usePublishStatus";
import { useHerdErrorDetail } from "../../hooks/useHerdErrorDetail";
import { StageContext } from "../../contexes/OrderIdContext";
import ModalFooter from "../common/ModalFooter";
import ErrorContentRenderer from "./ErrorContentRenderer";
import HerdErrorContent from "./HerdErrorContent";
import { getCBEPublishStatus } from "../../helpers/healthUtils";

function getSkipReasonDisplay(status?: PublishStatus | null): {
  label: string;
  description: string;
  indicator: StatusIndicatorProps.Type;
} {
  // Rows written before skipReason existed have none and were superseded.
  switch (status?.skipReason ?? "SUPERSEDED") {
    case "SUPERSEDED":
      return {
        label: "Skipped",
        description:
          "Not published because a newer version of this CBE was already published.",
        indicator: "info",
      };
    case "TOMBSTONED":
      return {
        label: "Tombstoned",
        description: `Not published because tombstone ${
          status?.tombstoneId ?? ""
        } covers this CBE.`,
        indicator: "stopped",
      };
    default:
      return {
        label: "Skipped",
        description: `Not published: ${status?.skipReason ?? ""}.`,
        indicator: "info",
      };
  }
}

interface PublishStatusProps {
  cbe: CanonicalBusinessEvent;
}

// date-fns durations stop at whole seconds, so anything shorter would format as "".
function formatSeconds(seconds: number): string {
  if (seconds < 1) {
    return `${Math.round(seconds * 1000)} ms`;
  }
  return formatDuration(intervalToDuration({ start: 0, end: seconds * 1000 }));
}

export default function PublishStatusComponent({ cbe }: PublishStatusProps) {
  const {
    isModalVisible,
    expandAll,
    handleModalOpen,
    handleModalClose,
    handleExpandAll,
    getCbeLinks,
    herdLink,
  } = usePublishStatus(cbe);

  const stage = useContext(StageContext);
  const herdDetailQuery = useHerdErrorDetail(
    cbe,
    stage,
    cbe.herdErrorDetailPending === true
  );

  function renderSuccessLinks() {
    const cbeLinks = getCbeLinks();
    const publishTime = cbe.publishStatus?.publishTime;
    const formattedTime = publishTime
      ? new Date(publishTime).toLocaleString()
      : null;
    const herdUnknown = cbe.herdStatusUnknown === true;
    const indicatorType = herdUnknown ? "warning" : "success";
    const publishedText = formattedTime
      ? `Published at ${formattedTime}`
      : "Publish Success";
    const statusText = herdUnknown
      ? "Published (Herd status unverified)"
      : publishedText;

    if (cbeLinks.length === 1) {
      return (
        <Link
          key="success-link"
          external
          externalIconAriaLabel="Opens in a new tab"
          href={cbeLinks[0].href}
          data-testid="FlashLink"
        >
          <StatusIndicator type={indicatorType}>{statusText}</StatusIndicator>
        </Link>
      );
    }
    return (
      <SpaceBetween direction="horizontal" size="xs">
        <StatusIndicator type={indicatorType}>{statusText}</StatusIndicator>
        {cbeLinks.map((link, index) => (
          <Link
            key={`success-link-${index}`}
            external
            externalIconAriaLabel="Opens in a new tab"
            href={link.href}
            data-testid={`FlashLink-${index}`}
          >
            {link.label}
          </Link>
        ))}
      </SpaceBetween>
    );
  }

  if (cbe.idempotenceId.includes("NONE")) {
    return <StatusIndicator type="info">NoOp</StatusIndicator>;
  }

  const publishStatus = getCBEPublishStatus(cbe);

  if (publishStatus.isUnpublished) {
    const completionDate = cbe.queueBurndown
      ? new Date(cbe.queueBurndown.estimatedCompletionTime)
      : null;

    const numberFormatter = new Intl.NumberFormat("en-US", {
      maximumFractionDigits: 0,
    });
    return (
      <StatusIndicator type="pending">
        Status Unknown/Pending Publish{" "}
        {completionDate ? (
          <Popover
            dismissButton={false}
            fixedWidth={false}
            position="bottom"
            triggerType="custom"
            content={
              <>
                Expected to be published{" "}
                <strong>
                  {formatDistanceToNow(
                    new Date(
                      Math.max(completionDate.getTime(), Date.now() + 1000)
                    ),
                    { addSuffix: true }
                  )}
                </strong>{" "}
                at {completionDate.toLocaleString()}
                <br />
                {cbe.queueBurndown!.streamDelaySeconds !== undefined ? (
                  <>
                    <strong>Average stream delay:</strong>{" "}
                    {formatSeconds(cbe.queueBurndown!.streamDelaySeconds)}
                    <br />
                  </>
                ) : null}
                <strong>Oldest item in queue:</strong>{" "}
                {formatSeconds(cbe.queueBurndown!.queueMaxAgeSeconds)}
                <br />
                <strong>Number of items in queue:</strong>{" "}
                {numberFormatter.format(cbe.queueBurndown!.queueDepth)}
                <br />
                <strong>Processing rate:</strong>{" "}
                {numberFormatter.format(cbe.queueBurndown!.queueProcessingRate)}
                /s
              </>
            }
          >
            <Button
              iconName="status-info"
              variant="icon"
              ariaLabel="View info"
            />
          </Popover>
        ) : null}
      </StatusIndicator>
    );
  }

  if (publishStatus.isSuccess) {
    return renderSuccessLinks();
  }

  if (publishStatus.isSkipped) {
    const publishTime = cbe.publishStatus?.publishTime;
    const formattedTime = publishTime
      ? new Date(publishTime).toLocaleString()
      : null;
    const { label, description, indicator } = getSkipReasonDisplay(
      cbe.publishStatus
    );
    return (
      <Popover
        dismissButton={false}
        position="bottom"
        triggerType="custom"
        content={description}
      >
        <StatusIndicator type={indicator}>
          {formattedTime ? `${label} at ${formattedTime}` : label}
        </StatusIndicator>
      </Popover>
    );
  }

  const herdFullDetail = cbe.herdErrorDetailPending
    ? herdDetailQuery.data ?? undefined
    : cbe.publishStatus?.stackTrace;
  // react-query v4 reports isLoading===true for a *disabled* query that never
  // fetched (e.g. a pending CBE with no realm), so gate on fetchStatus to avoid
  // a spinner that never resolves — same guard as useFetchSiblings.
  const herdDetailFetching = herdDetailQuery.fetchStatus === "fetching";
  const herdDetailLoading =
    cbe.herdErrorDetailPending === true &&
    !herdDetailQuery.data &&
    herdDetailFetching;
  const herdDetailFailed =
    cbe.herdErrorDetailPending === true &&
    !herdDetailQuery.data &&
    !herdDetailFetching &&
    (herdDetailQuery.isError || !cbe.realm);

  const errorDetails: ErrorDetails = parseErrorDetails(
    cbe.publishStatus?.stackTrace || "No error details",
    cbe.destination,
    cbe
  );

  return (
    <>
      <Button
        onClick={handleModalOpen}
        variant="inline-link"
        className="publish-status-button"
      >
        <StatusIndicator type="error">
          {cbe.herdFailed
            ? "Error Publishing - Failed in Herd"
            : "Error Publishing"}
        </StatusIndicator>
      </Button>
      <Modal
        className="error-modal"
        onDismiss={handleModalClose}
        visible={isModalVisible}
        closeAriaLabel="Close modal"
        size="max"
        footer={
          <ModalFooter
            stackTrace={
              cbe.herdFailed
                ? herdFullDetail || cbe.herdState
                : errorDetails.stackTrace
            }
            inputRequestUrl={errorDetails.inputRequestUrl}
            herdLink={cbe.herdFailed ? herdLink : undefined}
            onClose={handleModalClose}
            onExpandToggle={handleExpandAll}
            expandAll={expandAll}
          />
        }
        header={
          <Header
            variant="h2"
            description={
              cbe.publishStatus?.publishTime
                ? `Publish attempted at: ${new Date(
                    cbe.publishStatus.publishTime
                  ).toLocaleString()}`
                : undefined
            }
          >
            Error Type:{" "}
            {cbe.herdFailed
              ? "Failed in Herd"
              : errorDetails.errorType || "Unknown"}
          </Header>
        }
      >
        {cbe.herdFailed ? (
          <HerdErrorContent
            herdState={cbe.herdState}
            fullDetail={herdFullDetail}
            isLoadingDetail={herdDetailLoading}
            detailFailed={herdDetailFailed}
            expandAll={expandAll}
          />
        ) : (
          <ErrorContentRenderer
            errorDetails={errorDetails}
            isModalVisible={isModalVisible}
            expandAll={expandAll}
          />
        )}
      </Modal>
    </>
  );
}
